"use client";

import React from "react";
import { cn } from "@/lib/utils";
import InlineButton from "@/components/ui/inline-button";
import Image from "next/image";
import { useWindowSize } from "@/hooks/use-window-size";
import Link from "next/link";

export interface TextCardImageLeftProps {
  image: { src: string; alt: string };
  title?: string;
  description?: string;
  className?: string;
  button: {
    children: string;
    href: string;
  };
}

const TextCardImageLeft: React.FC<TextCardImageLeftProps> = ({
  image,
  title,
  description,
  className,
  button,
}) => {
  const size = useWindowSize();

  const anchorText = title
    ? `Navigate to ${title}`
    : `Go to ${button.children}`;

  const sharedClassName = cn(
    "overflow-hidden rounded-[20px] border border-neutral-200 shadow-[1px_2px_12px_2px_#524D4B1A] hover:border-primary-200 hover:bg-primary-100 hover:shadow-none md:shadow-[2px_4px_16px_2px_#524D4B1A] lg:shadow-[4px_8px_24px_2px_#524D4B1A]",
    "transition-all duration-200",
    button.href && "cursor-pointer",
    className
  );

  const cardContent = (
    <>
      <Image
        src={image.src}
        alt={image.alt}
        height={500}
        width={700}
        className="h-[140px] object-cover md:h-[160px] lg:h-[180px]"
      />
      <div className="flex flex-col gap-2 p-4">
        <h2 className="font-montserrat text-subheading-3-500 text-neutral-600">
          {title}
        </h2>
        <p className="line-clamp-2 font-roboto text-body-1-300 text-neutral-500">
          {description}
        </p>
        <InlineButton
          color="teal"
          size={size === "sm" ? "sm" : "md"}
        >
          {button.children}
        </InlineButton>
      </div>
    </>
  );

  if (button.href) {
    return (
      <Link
        href={button.href}
        prefetch
        className={cn("block", sharedClassName)}
        aria-label={anchorText}
      >
        {cardContent}
      </Link>
    );
  }

  return <div className={sharedClassName}>{cardContent}</div>;
};

export default TextCardImageLeft;
